import { useAuth } from "@clerk/clerk-react";
import { Box, Chip, Grid, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";
import { EmailSettings } from "schemas/email";
import { CustomEvent } from "schemas/functions";

import FollowUpDelayDivider from "components/FollowUpDelayDivider";
import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import QuestionBox from "components/Styled/VAPage/QuestionBox";
import FollowupSelect from "components/TemplateScreen/FollowupSelect";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import {
  DAYS_TO_DELAY_FOLLOWUP,
  MAXIMUM_FOLLOWUP_ALLOWED,
} from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import CreateTemplateDialog from "features/Influencer/HomePage/CreateTemplateDialog";
import EditTemplateDialog from "features/Influencer/Templates/TemplatesTable/EditTemplate";
import { fetcherAuth } from "utils/api";

import StyledProPage from "../../ProOnboarding/StyledPage";

const styles = {
  chip: {
    color: "green.main",
    textTransform: "uppercase",
    width: "max-content",
    backgroundColor: "white",
    border: "1px solid",
  },
  addButton: {
    mt: 2,
    px: 2,
    width: "max-content",
    backgroundColor: "white",
    border: "1px solid",
    color: "black",
    "&:hover": {
      backgroundColor: "white",
      border: "1px solid",
      color: "black",
    },
  },
};

interface Props {
  handleNext: () => void;
  handleBack: () => void;
  onFinishSetUpLater?: () => void;
  buttonNextLabel?: string;
}

// Shared by both Pro + Autopilot Onboarding
const OnboardingTemplatesForm = ({
  handleNext,
  handleBack,
  onFinishSetUpLater,
  buttonNextLabel = "Continue To Campaigns",
}: Props) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    editTemplateDialogId,
    setEditTemplateDialogId,
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    setCurrentTemplateContentType,
    fetchOutreachTemplates,
  } = useContext(OutreachTemplatesContext);

  const { setErrorAlert } = useContext(AlertContext);
  const {
    createTemplateOnboardingDialogOpen,
    setCreateTemplateOnboardingDialogOpen,
  } = useContext(OnboardingDialogsContext);

  const [modifiedTemplates, setModifiedTemplates] = useState<Template[]>([]);
  const [modifiedTemplateDelayDays, setModifiedTemplateDelayDays] = useState<
    (number | null)[]
  >([]);
  const [emailSetting, setEmailSetting] = useState<EmailSettings | null>(null);
  const [removeLoadingIndex, setRemoveLoadingIndex] = useState<number | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const templateFromEmailSettings = emailSetting?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const selectedFollowups = modifiedTemplates?.slice(
    1,
    modifiedTemplates?.length,
  );

  const fetchDefaultEmailSettings = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-settings/default`,
        "GET",
      );
      if (res.emailSetting) {
        setEmailSetting(res.emailSetting);
        const savedDelays =
          res.emailSetting.emailSettingTemplates?.map(
            (est: { delayDays?: number | null; emailOrder: number }) =>
              est.emailOrder === 0 ? null : est.delayDays ?? null,
          ) ?? null;
        if (res.emailSetting.templates) {
          setModifiedTemplates(res.emailSetting.templates);
          if (savedDelays) {
            setModifiedTemplateDelayDays(savedDelays);
          } else {
            setModifiedTemplateDelayDays(
              res.emailSetting.templates.map((_: Template, idx: number) =>
                idx === 0 ? null : DAYS_TO_DELAY_FOLLOWUP,
              ),
            );
          }
        } else {
          if (modifiedTemplates?.length === 0) {
            let defaultTemplates = [];
            if (filteredOutreachTemplates?.length > 0) {
              defaultTemplates.push(filteredOutreachTemplates[0]);
            }
            if (filteredFollowUps?.length > 0) {
              defaultTemplates.push(filteredFollowUps[0]);
            }
            setModifiedTemplates(defaultTemplates);
            setModifiedTemplateDelayDays(
              defaultTemplates.map((_, idx) =>
                idx === 0 ? null : DAYS_TO_DELAY_FOLLOWUP,
              ),
            );
          }
        }
      }
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleSaveDefaultTemplates = async (
    modifiedTemplates: Template[],
    modifiedTemplateDelayDays: (number | null)[],
    moveNext = true,
  ) => {
    try {
      setLoading(true);
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-settings/${emailSetting?.id}`,
        "PUT",
        {},
        {
          templateIds: modifiedTemplates.map((template) => template.id),
          templateDelayDays: modifiedTemplateDelayDays,
          source: "Pro Onboarding",
        },
      );
      if (moveNext) handleNext();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTemplate = (contentType: TemplateContentType) => {
    setCurrentTemplateContentType(contentType);
    setCreateTemplateOnboardingDialogOpen(true);
  };

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const template = allTemplates?.find((x: Template) => x.id === value);
    if (template) {
      const copy = [...modifiedTemplates];
      copy[index] = template;
      setModifiedTemplates(copy);
    }
  };

  const removeFollowUp = async (index: number) => {
    const copy = [...modifiedTemplates];
    copy.splice(index, 1);
    setModifiedTemplates(copy);
    const delayCopy = [...modifiedTemplateDelayDays];
    delayCopy.splice(index, 1);
    setModifiedTemplateDelayDays(delayCopy);
    setRemoveLoadingIndex(index);
    await handleSaveDefaultTemplates(copy, delayCopy, false);
    setRemoveLoadingIndex(null);
  };

  const addFollowUp = () => {
    setModifiedTemplates([...modifiedTemplates, followupTemplates?.[0]]);
    setModifiedTemplateDelayDays([
      ...modifiedTemplateDelayDays,
      DAYS_TO_DELAY_FOLLOWUP,
    ]);
  };

  const handleDelayChange = (index: number, value: number) => {
    const copy = [...modifiedTemplateDelayDays];
    copy[index] = value;
    setModifiedTemplateDelayDays(copy);
  };

  useEffect(() => {
    if (currentOrg?.id) {
      if (allTemplates?.length === 0) {
        fetchOutreachTemplates();
      }
      fetchDefaultEmailSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, allTemplates?.length]);

  const body = (
    <Grid
      container
      justifyContent="center"
      alignItems={"center"}
      flexDirection="column"
      gap={2}
    >
      <QuestionBox styles={{ width: "100%" }}>
        <Grid container alignItems={"center"} gap={1} sx={{ mb: 3 }}>
          <Chip label="Outreach" sx={styles.chip} variant="outlined" />
          <Typography color="textSecondary">
            Let's setup your outreach template
          </Typography>
        </Grid>

        {modifiedTemplates[0] ? (
          <OutreachSelect
            templates={modifiedTemplates}
            handleChange={handleTemplateChange}
            filteredOutreachTemplates={filteredOutreachTemplates}
            templateFromEmailSettings={templateFromEmailSettings}
            addNewOption={() =>
              handleGenerateTemplate(TemplateContentType.OUTREACH)
            }
          />
        ) : (
          <StyledLargeButton
            onClick={() => handleGenerateTemplate(TemplateContentType.OUTREACH)}
            disableElevation
            fullWidth
          >
            Generate Template
          </StyledLargeButton>
        )}
      </QuestionBox>

      <QuestionBox styles={{ width: "100%" }}>
        <Grid container alignItems={"center"} gap={1} sx={{ mb: 3 }}>
          <Chip label="Follow-up" sx={styles.chip} variant="outlined" />
          <Typography color="textSecondary">
            Let's setup your followup templates
          </Typography>
        </Grid>

        {followupTemplates.length === 0 ? (
          <StyledLargeButton
            disableElevation
            disabled={outreachTemplates.length === 0}
            onClick={() => handleGenerateTemplate(TemplateContentType.FOLLOWUP)}
            fullWidth
          >
            Generate Follow-up
          </StyledLargeButton>
        ) : (
          <Grid container flexDirection={"column"}>
            {selectedFollowups &&
              selectedFollowups.length > 0 &&
              selectedFollowups.map((followup, index) => (
                <Box key={index}>
                  <Grid container justifyContent="flex-start">
                    <FollowUpDelayDivider
                      days={
                        modifiedTemplateDelayDays[index + 1] ??
                        DAYS_TO_DELAY_FOLLOWUP
                      }
                      onChange={(val) => handleDelayChange(index + 1, val)}
                      isFirst={index === 0}
                    />
                  </Grid>
                  <FollowupSelect
                    followup={followup}
                    index={index}
                    filteredFollowUps={filteredFollowUps}
                    templateFromEmailSettings={templateFromEmailSettings}
                    handleTemplateChange={(e: CustomEvent, idx: number) =>
                      handleTemplateChange(e, idx)
                    }
                    onRemove={() => removeFollowUp(index + 1)}
                    removeLoading={removeLoadingIndex === index + 1}
                  />
                </Box>
              ))}

            {modifiedTemplates.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
              <Grid item xs={12}>
                <StyledLargeButton
                  variant="outlined"
                  disableElevation
                  onClick={addFollowUp}
                  styles={styles.addButton}
                >
                  <Box
                    component="i"
                    className="fa-regular fa-add"
                    sx={{ mr: 0.5 }}
                  />{" "}
                  Follow Up
                </StyledLargeButton>
              </Grid>
            )}
          </Grid>
        )}
      </QuestionBox>
    </Grid>
  );

  return (
    <>
      <StyledProPage
        title="Templates"
        description="Use an existing template, or create a new one with Bento AI"
        onBack={handleBack}
        onNext={() =>
          handleSaveDefaultTemplates(
            modifiedTemplates,
            modifiedTemplateDelayDays,
          )
        }
        buttonNext={buttonNextLabel}
        buttonBack="Back"
        body={body}
        stepNumber={2}
        disabledNext={!modifiedTemplates[0]}
        loading={loading}
        onFinishSetUpLater={onFinishSetUpLater}
      />

      {createTemplateOnboardingDialogOpen && (
        <CreateTemplateDialog isDialog={true} />
      )}

      {editTemplateDialogId && (
        <EditTemplateDialog
          propsTemplateId={editTemplateDialogId}
          propsHandleClose={() => {
            setEditTemplateDialogId(null);
          }}
        />
      )}
    </>
  );
};

export default OnboardingTemplatesForm;
